import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import styled, { css } from 'styled-components';

import {
  flexBoxCenter,
  justifyContentFlexStart,
  scrollbarX,
} from 'shared/styles/commonStyles';
import Footer from 'shared/components/Footer';
import Header from 'shared/components/Header';
import Sidebar from 'shared/components/sidebar/Sidebar';
import GlobalOverviewMain from 'features/globalOverview/components/GlobalOverViewMain';
import TelemetryMain from 'features/telemetry/components/TelemetryMain';
import EssMain from 'features/switchSystem/components/EssMain';
import SwitchMain from 'features/switchSystem/components/SwitchMain';
import TgsMain from 'features/switchSystem/components/TgsMain';
import TesMain from 'features/switchSystem/components/TesMain';
import HeatingPlatformMain from 'features/heatingPlatform/components';
import SettingsMain from 'features/settings/components/SettingsMain';
import AuditTrailMain from 'features/auditTrail/components/AuditTrailMain';
import FaultsMain from 'features/faults/components/FaultsMain';
import ReportStatusMain from 'features/reportStatus/components/ReportStatusMain';
import MobileMain from 'shared/components/mobileMain/MobileMain';
import MainLoadingPage from 'shared/components/loading/MainLoadingPage';

import { useAutoLogout, useBootstrapSystemsData } from 'shared/hooks';
import HomePage from 'features/landingPage/components/landingPage/HomePage';
import {
  RedesignLandingPage,
  RedesignLogin,
  RedesignResetPassword,
} from 'features/landingPage/components/redesign';
import EssTgsTesProvider from 'shared/context/contextOfEssTgsTes';
import GeneralProvider from 'shared/context/contextOfGeneral';

const MainPage = () => {
  const isMobile = useMediaQuery({ query: '(max-width:600px)' });
  const isTablet = useMediaQuery({ query: '(max-width:1366px)' });
  const {
    accessToken,
    storedAccessToken,
    isDataLoading,
    essRefetch,
    tgsRefetch,
    tesRefetch,
  } = useBootstrapSystemsData(isMobile);

  useAutoLogout(accessToken);

  const [savedPrevParam, setSavedPrevParam] = useState('');

  useEffect(() => {
    const filtersFromParams = new URLSearchParams(window.location.search);
    const path = filtersFromParams.get('path');

    if (path) {
      setSavedPrevParam(path);
    }
  }, []);

  if (isDataLoading) {
    return (
      <LoadingWrapper>
        <MainLoadingPage />
      </LoadingWrapper>
    );
  }



  const publicRoutes = (
    <PublicWrapper>
      <Routes>
        <Route path='/' element={<RedesignLandingPage />} />
        <Route path='/login' element={<RedesignLogin />} />
        <Route path='/login/fr' element={<RedesignLogin />} />
        <Route path='/reset-password' element={<RedesignResetPassword />} />
        <Route path='/login-legacy' element={<HomePage />} />
        <Route path='*' element={<Navigate to='/' />} />
      </Routes>
    </PublicWrapper>
  );

  return (
    <BrowserRouter>
      {isMobile ? (
        accessToken || storedAccessToken ? (
          <MobileWrapper>
            <GeneralProvider>
              <Header />
              <MobileMainContentsWrapper>
                <EssTgsTesProvider>
                  <Routes>
                    <Route path='/' element={<MobileMain />} />
                    <Route path='ess' element={<EssMain />} />
                    <Route path='tgs' element={<TgsMain />} />
                    <Route path='tes' element={<TesMain />} />

                    <Route path='masterControl' element={<Navigate to='/' />} />
                    <Route path='telemetry' element={<TelemetryMain />} />

                    <Route
                      path='heatingPlatform'
                      element={<HeatingPlatformMain />}
                    />

                    <Route
                      path='settings'
                      element={
                        <SettingsMain
                          essRefetch={essRefetch}
                          tgsRefetch={tgsRefetch}
                          tesRefetch={tesRefetch}
                        />
                      }
                    />
                    <Route path='faults' element={<FaultsMain />} />
                    <Route path='reportStatus' element={<ReportStatusMain />} />
                  </Routes>
                </EssTgsTesProvider>
              </MobileMainContentsWrapper>
              <Footer />
            </GeneralProvider>
          </MobileWrapper>
        ) : (
          publicRoutes
        )
      ) : accessToken || storedAccessToken ? (
        <MainWrapper isTablet={isTablet}>
          <GeneralProvider>
            <Wrapper>
              <Header />
              <Title src={'/images/embrellaTitle-sm.svg'} />
              <MainContentsWrapper>
                <Sidebar />
                <EssTgsTesProvider>
                  <Routes>
                    <Route path='/' element={<GlobalOverviewMain />} />

                    <Route path='/telemetry' element={<TelemetryMain />} />
                    <Route path='masterControl' element={<Navigate to='/' />} />

                    <Route path='ess' element={<SwitchMain swtName='ess' />} />
                    <Route path='tgs' element={<SwitchMain swtName='tgs' />} />
                    <Route path='tes' element={<SwitchMain swtName='tes' />} />

                    <Route
                      path='heatingPlatform'
                      element={<HeatingPlatformMain />}
                    />
                    <Route
                      path='settings'
                      element={
                        <SettingsMain
                          essRefetch={essRefetch}
                          tgsRefetch={tgsRefetch}
                          tesRefetch={tesRefetch}
                        />
                      }
                    />
                    <Route path='auditTrail' element={<AuditTrailMain />} />
                    <Route path='faults' element={<FaultsMain />} />
                    <Route path='reportStatus' element={<ReportStatusMain />} />
                    <Route
                      path='*'
                      element={<Navigate to={savedPrevParam} />}
                    />
                  </Routes>
                </EssTgsTesProvider>
              </MainContentsWrapper>
              <Footer />
            </Wrapper>
          </GeneralProvider>
        </MainWrapper>
      ) : (
        publicRoutes
      )}
    </BrowserRouter>
  );
};

export default MainPage;

const MainWrapper = styled.div`
  ${({ isTablet }) =>
    isTablet
      ? css`
          width: 100%;
          height: 100%;
          ${scrollbarX}
        `
      : css`
          width: 100%;
          ${flexBoxCenter}
        `}
`;

const Wrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;


  padding: 10rem 20rem;
  padding-top: 0;
`;



const PublicWrapper = styled.div`
  width: 100%;
  min-height: 100vh;

  min-height: 100dvh;
`;

const LoadingWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  z-index: 1000;
`;

const Title = styled.img`
  margin: 5rem 0;
`;

const MainContentsWrapper = styled.div`
  width: 100%;
  height: auto;

  display: flex;
  align-items: flex-start;
  gap: 24rem;

  margin: 5rem 0;


  & > *:last-child {
    flex: 1;
    min-width: 0;
  }
`;

const MobileWrapper = styled.div`
  /* 332px is the top of a hardcoded width cascade (332/328/326/319/316/312)
     running through MobileMain, Header, Footer and MobileMasterControl, so
     widening this alone would not make the children fluid. Centering it keeps
     the leftover space symmetric instead of pooling in a right-hand gutter.
     Converting the full cascade to percentage widths is the follow-up. */
  width: 332px;
  max-width: 100%;
  margin: 0 auto;

  /* Keep content clear of the notch and the home indicator. Paired with
     viewport-fit=cover in index.html; env() resolves to 0px off-iOS and in
     browsers that don't support it, so this is safe everywhere. */
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
  padding-bottom: env(safe-area-inset-bottom);

  ${justifyContentFlexStart}
  flex-direction: column;
`;

const MobileMainContentsWrapper = styled.div``;
